"use client";

import { useQuery } from "@tanstack/react-query";
import type { FormEventHandler } from "react";
import { useState } from "react";
import { HiGif } from "react-icons/hi2";
import { LuSearch } from "react-icons/lu";

import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerTitle,
} from "@/components/shared/DialogDrawer";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const KLIPY_API_KEY = process.env.NEXT_PUBLIC_KLIPY_API_KEY ?? "";
const KLIPY_CLIENT_KEY = "focus";
const DEFAULT_LIMIT = 10;

const getKlipyApiUrl = (
  endpoint: "featured" | "search",
  params: Record<string, string> = {},
) => {
  const searchParams = new URLSearchParams({
    key: KLIPY_API_KEY,
    client_key: KLIPY_CLIENT_KEY,
    limit: String(DEFAULT_LIMIT),
    ...params,
  });

  return `https://api.klipy.com/v2/${endpoint}?${searchParams.toString()}`;
};

const klipyApi = {
  featured: getKlipyApiUrl("featured"),
  search: (query: string) => getKlipyApiUrl("search", { q: query }),
};

type GifSearchProps = {
  onSelect: (url: string) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
};

type KlipyApiResponse = {
  results: {
    id: string;
    media_formats: {
      gif: {
        dims: [number, number];
        url: string;
      };
    };
    content_description: string;
  }[];
};

export const GifSearch = ({ onSelect, onOpenChange, open }: GifSearchProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const { data = [], isLoading } = useQuery({
    queryKey: ["gifsearch", searchTerm],
    queryFn: () => {
      if (!searchTerm) {
        return fetch(klipyApi.featured)
          .then((res) => res.json())
          .then((data: KlipyApiResponse) => data.results.slice(0, 10));
      }

      return fetch(klipyApi.search(searchTerm))
        .then((res) => res.json())
        .then((data: KlipyApiResponse) => data.results);
    },
    placeholderData: (prev) => prev,
  });

  const handleSubmit: FormEventHandler<HTMLFormElement> = (ev) => {
    ev.preventDefault();

    setSearchTerm(
      (new FormData(ev.currentTarget).get("search") as string).trim(),
    );
  };

  return (
    <DialogDrawer open={open} onOpenChange={onOpenChange}>
      <DialogDrawerContent
        className="
          w-full max-w-full! overflow-auto
          lg:w-[50vw]
        "
      >
        <DialogDrawerTitle
          className="
            flex items-center justify-center
            md:justify-start
          "
        >
          <HiGif className="mr-2 text-xl text-muted" />
          Add a GIF{" "}
          <span className="pl-2 text-sm opacity-50">
            Powered By{" "}
            <a
              href="https://klipy.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-muted-foreground"
            >
              KLIPY
            </a>
          </span>
        </DialogDrawerTitle>
        <DialogDrawerDescription className="hidden">
          Gif Search
        </DialogDrawerDescription>
        <div className="border-b p-4">
          <form onSubmit={handleSubmit} className="flex w-full gap-4">
            <Input placeholder="Search KLIPY" name="search" />
            <Button type="submit">
              <LuSearch />
            </Button>
          </form>
        </div>
        <div
          className="
            columns-1 gap-4 overflow-auto p-4
            lg:columns-3
          "
        >
          {!data.length && !searchTerm && isLoading && "Loading trending GIFs"}
          {!data.length && searchTerm && isLoading && "Loading GIFs"}
          {data.map((item) => {
            const gif = item.media_formats.gif;
            return (
              <div
                key={item.id}
                className="
                  mb-4 cursor-pointer rounded-2xl border-4 border-background
                  bg-card-foreground
                  hover:border-primary hover:shadow-xl hover:shadow-card
                "
                style={{
                  aspectRatio: `${gif.dims[0]} / ${gif.dims[1]}`,
                }}
                onClick={() => {
                  onSelect(gif.url);
                  onOpenChange(false);
                }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  className="w-full rounded-xl"
                  src={gif.url}
                  alt={item.content_description}
                />
              </div>
            );
          })}
        </div>
        {!isLoading && !data.length && (
          <div className="py-24">
            <EmptyState
              image="/assets/empty-15.png"
              title="We couldn't find any GIFs."
              description="Search for something else."
            />
          </div>
        )}
      </DialogDrawerContent>
    </DialogDrawer>
  );
};
